import { zCurrency } from "@redotech/redo-billing-common/currency";
import { SurchargeCategory } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum SurchargeSource {
  // Easypost is USPS
  Easypost = "easypost",
  Amazon = "amazon",
  Fedex = "fedex",
  Unishippers = "ups",
  UpsDirect = "ups-direct",
  IB = "international-bridge",
  FirstMile = "first-mile",
  Usps = "usps",
  Dhl = "dhl",
}

export const surchargeCategoryLabels: Record<SurchargeCategory, string> = {
  [SurchargeCategory.Weight]: "Weight",
  [SurchargeCategory.Dimensions]: "Dimensions",
  [SurchargeCategory.FuelSurcharge]: "Fuel Surcharge",
  [SurchargeCategory.DeliveryArea]: "Delivery Area",
  [SurchargeCategory.International]: "International",
  [SurchargeCategory.AddressCorrection]: "Address Correction",
  [SurchargeCategory.PackageType]: "Package Type",
  [SurchargeCategory.Other]: "Other",
};

export const getBaseUniqueIdentifier = (
  source: SurchargeSource,
  shipmentId: string,
) => {
  return source + ":" + shipmentId + ":";
};

export const getDhlUniqueIdentifier = (
  trackingCode: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Dhl, trackingCode) + description
  );
};

// EasyPost (USPS) price adjustments have an id
export const getEasypostUniqueIdentifier = (
  shipmentId: string,
  paylogId: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Easypost, shipmentId) + paylogId
  );
};

// Amazon price adjustment descriptions should be unique per shipment
export const getAmazonUniqueIdentifier = (
  shipmentId: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Amazon, shipmentId) + description
  );
};

export const getFedexUniqueIdentifier = (
  shipmentId: string,
  invoiceNumber?: string,
  description?: string,
) => {
  let id = getBaseUniqueIdentifier(SurchargeSource.Fedex, shipmentId);
  if (invoiceNumber) {
    id += invoiceNumber;
  }
  if (description) {
    id += ":" + description;
  }
  return id;
};

// UPS price adjustment descriptions should be unique and shipment id
export const getUpsUniqueIdentifier = (
  shipmentId: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Unishippers, shipmentId) +
    description
  );
};

export const getIBUniqueIdentifier = (
  trackingNumber: string,
  comment: string,
) => {
  return getBaseUniqueIdentifier(SurchargeSource.IB, trackingNumber) + comment;
};

export const getFirstMileUniqueIdentifier = (
  trackingNumber: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.FirstMile, trackingNumber) +
    description
  );
};

export const getUpsDirectUniqueIdentifier = (
  trackingNumber: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.UpsDirect, trackingNumber) +
    description
  );
};

export const getUpsDirectAccountLevelUniqueIdentifier = (
  invoiceDate: string,
  billToAccount: string,
  description: string,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.UpsDirect, invoiceDate) +
    billToAccount +
    ":" +
    description
  );
};

export const getUspsUniqueIdentifier = (
  trackingNumber: string,
  postageVariance: number,
) => {
  return (
    getBaseUniqueIdentifier(SurchargeSource.Usps, trackingNumber) +
    postageVariance.toString()
  );
};

export const surchargeSchema_ = z.object({
  teamId: z.string(),
  source: createEnum(SurchargeSource, { enumName: "SurchargeSource" }),
  uniqueIdentifier: z.string(),
  fulfillmentGroupId: z.string().nullish(),
  shipmentId: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  date: z.date(),
  amount: z.string(),
  currency: zCurrency,
  description: z.string(),
  category: createEnum(SurchargeCategory, {
    enumName: "SurchargeCategory",
  }).default(SurchargeCategory.Other),
  loadedAt: z.date(),
  capturedDimensions: z
    .object({
      lengthInInches: z.number().nullish(),
      widthInInches: z.number().nullish(),
      heightInInches: z.number().nullish(),
      weightInOunces: z.number().nullish(),
      packageType: z.string().nullish(),
    })
    .nullish(),
});

export const SurchargeSchema: z.ZodType<Surcharge> = surchargeSchema_;
export interface Surcharge extends z.infer<typeof surchargeSchema_> {}

export function getQualifyingAlarmingSurcharges(
  surcharges: Surcharge[],
  selectedCategories: SurchargeCategory[],
  threshold: number | null | undefined,
): Surcharge[] {
  return surcharges.filter((surcharge) => {
    const categoryMatches =
      selectedCategories.length === 0 ||
      selectedCategories.includes(surcharge.category);
    const amountMatches =
      threshold == null || Math.abs(Number(surcharge.amount)) > threshold;
    return categoryMatches && amountMatches;
  });
}

const amazonAdjustmentSnowflakeResponseSchema_ = z.object({
  SHIPMENT_ID: z.string().nullish(),
  TRACKING_ID: z.string(),
  BASE_RATE_INCLUDING_TAX_AMOUNT_USD: z.number(),
  COMMENTS: z.string().nullish(),
  FULFILLMENT_GROUP_ID: z.string(),
  TEAM: z.string(),
  DATE_OF_CHARGE: z.date(),
  LOADED_AT: z.date(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().nullish(),
});
export const AmazonAdjustmentSnowflakeResponseSchema: z.ZodType<AmazonAdjustmentSnowflakeResponse> =
  amazonAdjustmentSnowflakeResponseSchema_;
export interface AmazonAdjustmentSnowflakeResponse
  extends z.infer<typeof amazonAdjustmentSnowflakeResponseSchema_> {}

const fedexSurchargeSnowflakeResponseSchema_ = z.object({
  INVOICE_DATE: z.date(),
  INVOICE_NUMBER: z.string(),
  EXPRESS_OR_GROUND_TRACKING_ID: z.string(),
  CONCATENATED_TRACKING_CODE: z.string(),
  TRANSPORTATION_CHARGE_AMOUNT: z.number().nullish(),
  NET_CHARGE_AMOUNT: z.number(),
  ACTUAL_WEIGHT_IN_POUNDS: z.number().nullish(),
  DIM_LENGTH_IN_INCHES: z.number().nullish(),
  DIM_WIDTH_IN_INCHES: z.number().nullish(),
  DIM_HEIGHT_IN_INCHES: z.number().nullish(),
  REPORTED_LENGTH: z.number().nullish(),
  REPORTED_WIDTH: z.number().nullish(),
  REPORTED_HEIGHT: z.number().nullish(),
  REPORTED_WEIGHT_OZ: z.number().nullish(),
  LOADED_AT: z.date(),
  TRACKING_ID_CHARGE_AMOUNT: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_1: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_1: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_2: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_2: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_3: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_3: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_4: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_4: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_5: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_5: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_6: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_6: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_7: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_7: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_8: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_8: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_9: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_9: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_10: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_10: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_11: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_11: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_12: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_12: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_13: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_13: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_14: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_14: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_15: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_15: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_16: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_16: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_17: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_17: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_18: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_18: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_19: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_19: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_20: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_20: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_21: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_21: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_22: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_22: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_23: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_23: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_24: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_24: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_25: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_25: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_26: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_26: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_27: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_27: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_28: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_28: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_29: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_29: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_30: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_30: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_31: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_31: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_32: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_32: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_33: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_33: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_34: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_34: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_35: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_35: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_36: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_36: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_37: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_37: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_38: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_38: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_39: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_39: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_40: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_40: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_41: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_41: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_42: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_42: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_43: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_43: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_44: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_44: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_45: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_45: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_46: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_46: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_47: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_47: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_48: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_48: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_49: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_49: z.string().nullish(),
  TRACKING_ID_CHARGE_AMOUNT_50: z.number().nullish(),
  TRACKING_ID_CHARGE_DESCRIPTION_50: z.string().nullish(),
  FULFILLMENT_GROUP_ID: z.string(),
  TEAM: z.string(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().nullish(),
  PROVIDER_COST: z.number(),
});
export const FedexSurchargeSnowflakeResponseSchema: z.ZodType<FedexSurchargeSnowflakeResponse> =
  fedexSurchargeSnowflakeResponseSchema_;
export interface FedexSurchargeSnowflakeResponse
  extends z.infer<typeof fedexSurchargeSnowflakeResponseSchema_> {}

//USPS
const easypostSurchargeSnowflakeResponseSchema_ = z.object({
  INVOICE_DATE: z.date(),
  SHIPMENT_ID: z.string().nullish(),
  TRACKING_CODE: z.string().nullish(),
  ADJUSTMENT_AMOUNT: z.number(),
  ADJUSTMENT_REASON: z.string().nullish(),
  CAPTURED_PARCEL_LENGTH: z.number().nullish(),
  CAPTURED_PARCEL_WIDTH: z.number().nullish(),
  CAPTURED_PARCEL_HEIGHT: z.number().nullish(),
  PARCEL_WEIGHT: z.number().nullish(),
  INVOICE_PAYMENT_LOG: z.string(),
  FULFILLMENT_GROUP_ID: z.string(),
  TEAM: z.string(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().nullish(),
});
export const EasypostSurchargeSnowflakeResponseSchema: z.ZodType<EasypostSurchargeSnowflakeResponse> =
  easypostSurchargeSnowflakeResponseSchema_;
export interface EasypostSurchargeSnowflakeResponse
  extends z.infer<typeof easypostSurchargeSnowflakeResponseSchema_> {}

const upsAdjustmentSnowflakeResponseSchema_ = z.object({
  FULFILLMENT_GROUP_ID: z.string(),
  TEAM_ID: z.string(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().nullish(),
  QUOTED_COST: z.number(),
  TRACKING_CODE: z.string(),
  TOTAL_CHARGE_AMOUNT: z.number(),
  CHARGES: z.array(
    z.object({
      charge_amount: z.coerce.number(),
      charge_type: z.string().nullish(),
      invoice_date: z.string(),
      original_weight: z.coerce.string().nullish(),
      charged_weight: z.coerce.string().nullish(),
      charge_number: z.number(),
    }),
  ),
  LOADED_AT: z.date(),
  SURCHARGE: z.number(),
});
export const UpsAdjustmentSnowflakeResponseSchema: z.ZodType<UnishippersAdjustmentSnowflakeResponse> =
  upsAdjustmentSnowflakeResponseSchema_;
export interface UnishippersAdjustmentSnowflakeResponse
  extends z.infer<typeof upsAdjustmentSnowflakeResponseSchema_> {}

const ibAdjustmentSnowflakeResponseSchema_ = z.object({
  ASSIGNED_FULFILLMENT_SERVICE: z.string(),
  FULFILLMENT_GROUP_ID: z.string(),
  ADDITIONAL_POSTAGE_DUE: z.number(),
  COMMENT: z.string(),
  TRACKING_NUMBER: z.string(),
  RECALCULATED_POSTAGE: z.number(),
  DATE_OF_SAMPLING: z.date(),
  WEIGHT: z.number(),
  WEIGHT_UNIT: z.string(),
  LENGTH: z.number(),
  WIDTH: z.number(),
  HEIGHT: z.number(),
  DIMENSIONS_UNIT: z.string(),
});
export const IbAdjustmentSnowflakeResponseSchema: z.ZodType<IbAdjustmentSnowflakeResponse> =
  ibAdjustmentSnowflakeResponseSchema_;
export interface IbAdjustmentSnowflakeResponse
  extends z.infer<typeof ibAdjustmentSnowflakeResponseSchema_> {}

const firstMileSurchargeSnowflakeResponseSchema_ = z.object({
  TEAM: z.string().nullish(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string(),
  FULFILLMENT_GROUP_ID: z.string(),
  INVOICE_DATE: z.date(),
  INVOICE_NUMBER: z.string().nullish(),
  TRACKING_NUMBER: z.string(),
  SHIPMENT_ID: z.string().nullish(),
  TOTAL_CHARGE: z.number().nullish(),
  FM_BASE_RATE: z.number().nullish(),
  ZONE: z.string().nullish(),
  WEIGHT_OZ: z.number().nullish(),
  WEIGHT_LBS: z.number().nullish(),
  FUEL: z.number().nullish(),
  DIM_LENGTH: z.number().nullish(),
  DIM_WIDTH: z.number().nullish(),
  DIM_HEIGHT: z.number().nullish(),
  ACTUAL_WEIGHT: z.number().nullish(),
  LOADED_AT: z.date(),
  SURCHARGE_1_TYPE: z.string().nullish(),
  SURCHARGE_1: z.number().nullish(),
  SURCHARGE_2_TYPE: z.string().nullish(),
  SURCHARGE_2: z.number().nullish(),
  SURCHARGE_3_TYPE: z.string().nullish(),
  SURCHARGE_3: z.number().nullish(),
  SURCHARGE_4_TYPE: z.string().nullish(),
  SURCHARGE_4: z.number().nullish(),
  SURCHARGE_5_TYPE: z.string().nullish(),
  SURCHARGE_5: z.number().nullish(),
  SURCHARGE_6_TYPE: z.string().nullish(),
  SURCHARGE_6: z.number().nullish(),
  SURCHARGE_7_TYPE: z.string().nullish(),
  SURCHARGE_7: z.number().nullish(),
  SURCHARGE_8_TYPE: z.string().nullish(),
  SURCHARGE_8: z.number().nullish(),
  SURCHARGE_9_TYPE: z.string().nullish(),
  SURCHARGE_9: z.number().nullish(),
  SURCHARGE_10_TYPE: z.string().nullish(),
  SURCHARGE_10: z.number().nullish(),
  SURCHARGE_11_TYPE: z.string().nullish(),
  SURCHARGE_11: z.number().nullish(),
  SURCHARGE_12_TYPE: z.string().nullish(),
  SURCHARGE_12: z.number().nullish(),
  SURCHARGE_13_TYPE: z.string().nullish(),
  SURCHARGE_13: z.number().nullish(),
  SURCHARGE_14_TYPE: z.string().nullish(),
  SURCHARGE_14: z.number().nullish(),
  SURCHARGE_15_TYPE: z.string().nullish(),
  SURCHARGE_15: z.number().nullish(),
  SURCHARGE_16_TYPE: z.string().nullish(),
  SURCHARGE_16: z.number().nullish(),
  SURCHARGE_17_TYPE: z.string().nullish(),
  SURCHARGE_17: z.number().nullish(),
  SURCHARGE_18_TYPE: z.string().nullish(),
  SURCHARGE_18: z.number().nullish(),
  SURCHARGE_19_TYPE: z.string().nullish(),
  SURCHARGE_19: z.number().nullish(),
  SURCHARGE_20_TYPE: z.string().nullish(),
  SURCHARGE_20: z.number().nullish(),
  SURCHARGE_21_TYPE: z.string().nullish(),
  SURCHARGE_21: z.number().nullish(),
  SURCHARGE_22_TYPE: z.string().nullish(),
  SURCHARGE_22: z.number().nullish(),
  SURCHARGE_23_TYPE: z.string().nullish(),
  SURCHARGE_23: z.number().nullish(),
  SURCHARGE_24_TYPE: z.string().nullish(),
  SURCHARGE_24: z.number().nullish(),
  SURCHARGE_25_TYPE: z.string().nullish(),
  SURCHARGE_25: z.number().nullish(),
  SURCHARGE_26_TYPE: z.string().nullish(),
  SURCHARGE_26: z.number().nullish(),
  SURCHARGE_27_TYPE: z.string().nullish(),
  SURCHARGE_27: z.number().nullish(),
  // Will we get 27 surcharges? Probably not, but that is the schema
});
export const FirstMileSurchargeSnowflakeResponseSchema: z.ZodType<FirstMileSurchargeSnowflakeResponse> =
  firstMileSurchargeSnowflakeResponseSchema_;
export interface FirstMileSurchargeSnowflakeResponse
  extends z.infer<typeof firstMileSurchargeSnowflakeResponseSchema_> {}

const upsDirectLabelSurchargeSnowflakeResponseSchema_ = z.object({
  SERVICE_NAME: z.string().nullish(),
  TRACKING_NUMBER: z.string(),
  INVOICE_DATE: z.date().nullish(),
  CURRENCY: z.string().nullish(),
  TOTAL_COST: z.number().nullish(),
  FREIGHT_CHARGE: z.number().nullish(),
  FUEL_CHARGE: z.number().nullish(),
  SHIPMENT_WEIGHT: z.coerce.number().nullish(),
  SHIPMENT_WEIGHT_UNIT: z.string().nullish(),
  SHIPMENT_HEIGHT: z.coerce.number().nullish(),
  SHIPMENT_WIDTH: z.coerce.number().nullish(),
  SHIPMENT_LENGTH: z.coerce.number().nullish(),
  SHIPMENT_DIMENSION_UNIT: z.string().nullish(),
  KEY_ENTERED_WEIGHT: z.coerce.number().nullish(),
  KEY_ENTERED_WEIGHT_UNIT: z.string().nullish(),
  KEY_ENTERED_HEIGHT: z.coerce.number().nullish(),
  KEY_ENTERED_WIDTH: z.coerce.number().nullish(),
  KEY_ENTERED_LENGTH: z.coerce.number().nullish(),
  DIMENSION_UNIT: z.string().nullish(),
  ZONE: z.string().or(z.number()).transform(String).nullish(),
  ADDITIONAL_CHARGE_1_DESC: z.string().nullish(),
  ADDITIONAL_CHARGE_1: z.number().nullish(),
  ADDITIONAL_CHARGE_2_DESC: z.string().nullish(),
  ADDITIONAL_CHARGE_2: z.number().nullish(),
  ADDITIONAL_CHARGE_3_DESC: z.string().nullish(),
  ADDITIONAL_CHARGE_3: z.number().nullish(),
  ADDITIONAL_CHARGE_4_DESC: z.string().nullish(),
  ADDITIONAL_CHARGE_4: z.number().nullish(),
  ADDITIONAL_CHARGE_5_DESC: z.string().nullish(),
  ADDITIONAL_CHARGE_5: z.number().nullish(),
  ADDITIONAL_CHARGE_6_DESC: z.string().nullish(),
  ADDITIONAL_CHARGE_6: z.number().nullish(),
  OTHER_ADDITIONAL_CHARGES: z.string().nullish(),
  OTHER_ADDITIONAL_CHARGES_AMOUNT: z.number().nullish(),
  VAT: z.number().nullish(),
  TAX: z.number().nullish(),
  DUTY: z.number().nullish(),
  BROKERAGE: z.number().nullish(),
  LOADED_AT: z.date(),
  FULFILLMENT_GROUP_ID: z.string(),
  TEAM: z.string(),
  PROVIDER_COST: z.number(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string().nullish(),
  IS_DDP: z.boolean().nullish(),
});
export const UpsDirectLabelSurchargeSnowflakeResponseSchema: z.ZodType<UpsDirectLabelSurchargeSnowflakeResponse> =
  upsDirectLabelSurchargeSnowflakeResponseSchema_;
export interface UpsDirectLabelSurchargeSnowflakeResponse
  extends z.infer<typeof upsDirectLabelSurchargeSnowflakeResponseSchema_> {}

const upsDirectAccountLevelSurchargeSnowflakeResponseSchema_ = z.object({
  SERVICE_NAME: z.string(),
  BILL_TO_ACCOUNT: z.string(),
  TOTAL_COST: z.number(),
  INVOICE_DATE: z.date(),
  LOADED_AT: z.date(),
});
export const UpsDirectAccountLevelSurchargeSnowflakeResponseSchema: z.ZodType<UpsDirectAccountLevelSurchargeSnowflakeResponse> =
  upsDirectAccountLevelSurchargeSnowflakeResponseSchema_;
export interface UpsDirectAccountLevelSurchargeSnowflakeResponse
  extends z.infer<
    typeof upsDirectAccountLevelSurchargeSnowflakeResponseSchema_
  > {}

// USPS
const uspsAdjustmentSnowflakeResponseSchema_ = z.object({
  _ID: z.string(),
  TRACKING_NUMBER: z.string(),
  TEAM: z.string(),
  FULFILLMENT_GROUP_ID: z.string(),
  POSTAGE_VARIANCE: z.number(),
  ROOT_CAUSE: z.array(z.string()).nullish(),
  TOTAL_POSTAGE_CLAIMED: z.string().nullish(),
  TOTAL_POSTAGE_ASSESSED: z.string().nullish(),
  BASE_POSTAGE_CLAIMED: z.string().nullish(),
  BASE_POSTAGE_ASSESSED: z.string().nullish(),
  ACCOUNT_NUMBER: z.string().nullish(),
  TRANSACTION_ID: z.string().nullish(),
  MID: z.string().nullish(),
  CRID: z.string().nullish(),
  ADJUSTMENT_STATUS: z.string().nullish(),
  CREATED_AT: z.date(),
  UPDATED_AT: z.date().nullish(),
  SYNC_TO_SNOWFLAKE_AT: z.date().nullish(),
  DESTINATION_ENTRY_FACILITY_TYPE_CLAIMED: z.string().nullish(),
  DESTINATION_ENTRY_FACILITY_TYPE_ASSESSED: z.string().nullish(),
  DESTINATION_ZIP_CODE_CLAIMED: z.string().nullish(),
  DESTINATION_ZIP_CODE_ASSESSED: z.string().nullish(),
  DIM_WEIGHT_CLAIMED: z.string().nullish(),
  DIM_WEIGHT_ASSESSED: z.string().nullish(),
  HEIGHT_CLAIMED: z.string().nullish(),
  HEIGHT_ASSESSED: z.string().nullish(),
  LENGTH_CLAIMED: z.string().nullish(),
  LENGTH_ASSESSED: z.string().nullish(),
  MAIL_CLASS_CLAIMED: z.string().nullish(),
  MAIL_CLASS_ASSESSED: z.string().nullish(),
  ORIGIN_ZIP_CODE_CLAIMED: z.string().nullish(),
  ORIGIN_ZIP_CODE_ASSESSED: z.string().nullish(),
  RATE_INDICATOR_CLAIMED: z.string().nullish(),
  RATE_INDICATOR_ASSESSED: z.string().nullish(),
  WEIGHT_CLAIMED: z.string().nullish(),
  WEIGHT_ASSESSED: z.string().nullish(),
  WIDTH_CLAIMED: z.string().nullish(),
  WIDTH_ASSESSED: z.string().nullish(),
  ZONE_CLAIMED: z.string().nullish(),
  ZONE_ASSESSED: z.string().nullish(),
});
export const UspsAdjustmentSnowflakeResponseSchema: z.ZodType<UspsAdjustmentSnowflakeResponse> =
  uspsAdjustmentSnowflakeResponseSchema_;
export interface UspsAdjustmentSnowflakeResponse
  extends z.infer<typeof uspsAdjustmentSnowflakeResponseSchema_> {}

const dhlAdjustmentSnowflakeResponseSchema_ = z.object({
  TRACKING_CODE: z.string(),
  FULFILLMENT_GROUP_ID: z.string(),
  ASSIGNED_FULFILLMENT_SERVICE: z.string(),
  INVOICE_DATE: z.date(),
  DIM_HEIGHT: z.number().nullish(),
  DIM_LENGTH: z.number().nullish(),
  DIM_WIDTH: z.number().nullish(),
  DIM_WEIGHT: z.number().nullish(),
  DIM_UNIT: z.string().nullish(),
  ACTUAL_WEIGHT: z.number().nullish(),
  ACTUAL_WEIGHT_UNIT: z.string().nullish(),
  HEIGHT_IN_INCHES: z.number().nullish(),
  LENGTH_IN_INCHES: z.number().nullish(),
  WIDTH_IN_INCHES: z.number().nullish(),
  WEIGHT_IN_OZ: z.number().nullish(),
  DUTIES_AND_TAXES_1: z.number().nullish(),
  DUTIES_AND_TAXES_2: z.number().nullish(),
  DUTIES_AND_TAXES_3: z.number().nullish(),
  DUTIES_AND_TAXES_4: z.number().nullish(),
  DUTIES_AND_TAXES_5: z.number().nullish(),
  NONSTANDARD_SURCHARGE: z.number().nullish(),
  DELIVERY_AREA_SURCHARGE: z.number().nullish(),
  MINIMUM_ORDER_PICKUP_FEE: z.number().nullish(),
  DEMAND_SURCHARGE: z.number().nullish(),
  FUEL_SURCHARGE: z.number().nullish(),
  ACTUAL_LABEL_COST: z.number(),
  PROVIDER_COST: z.number(),
  BASE_CHARGE: z.number(),
  LOADED_AT: z.date(),
});
export const DhlAdjustmentSnowflakeResponseSchema: z.ZodType<DhlAdjustmentSnowflakeResponse> =
  dhlAdjustmentSnowflakeResponseSchema_;
export interface DhlAdjustmentSnowflakeResponse
  extends z.infer<typeof dhlAdjustmentSnowflakeResponseSchema_> {}

export const baseSurchargeRowSchema = z.object({
  source: createEnum(SurchargeSource, { enumName: "SurchargeSource" }),
  createdAt: z.date(),
  lastAttemptedAt: z.date(),
  accountLevel: z.boolean().nullish(),
});

const easypostSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Easypost),
  row: easypostSurchargeSnowflakeResponseSchema_,
});

const fedexSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Fedex),
  row: fedexSurchargeSnowflakeResponseSchema_,
});

const upsSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Unishippers),
  row: upsAdjustmentSnowflakeResponseSchema_,
});

const amazonSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Amazon),
  row: amazonAdjustmentSnowflakeResponseSchema_,
});

const ibSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.IB),
  row: ibAdjustmentSnowflakeResponseSchema_,
});

const firstMileSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.FirstMile),
  row: firstMileSurchargeSnowflakeResponseSchema_,
});

const upsDirectLabelSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.UpsDirect),
  accountLevel: z.literal(false).nullish(),
  row: upsDirectLabelSurchargeSnowflakeResponseSchema_,
});

const upsDirectAccountLevelSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.UpsDirect),
  accountLevel: z.literal(true),
  row: upsDirectAccountLevelSurchargeSnowflakeResponseSchema_,
});

const upsDirectSurchargeRowSchema = z.union([
  upsDirectLabelSurchargeRowSchema,
  upsDirectAccountLevelSurchargeRowSchema,
]);

const uspsSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Usps),
  row: uspsAdjustmentSnowflakeResponseSchema_,
});

const dhlSurchargeRowSchema = baseSurchargeRowSchema.extend({
  source: z.literal(SurchargeSource.Dhl),
  row: dhlAdjustmentSnowflakeResponseSchema_,
});

const surchargeRowSchema_ = z.union([
  easypostSurchargeRowSchema,
  fedexSurchargeRowSchema,
  upsSurchargeRowSchema,
  amazonSurchargeRowSchema,
  ibSurchargeRowSchema,
  firstMileSurchargeRowSchema,
  upsDirectSurchargeRowSchema,
  uspsSurchargeRowSchema,
  dhlSurchargeRowSchema,
]);
export const surchargeRowSchema: z.ZodType<SurchargeRow> = surchargeRowSchema_;
export type SurchargeRow = z.infer<typeof surchargeRowSchema_>;
